import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid, { GridSpacing } from '@material-ui/core/Grid';
import AlmKindRegistGridHeader from './AlmKindRegistGridHeader';
import AlmKindRegistGridBody from './AlmKindRegistGridBody';
import AlmKindRegistFooter from './AlmKindRegistFooter';
import { TableBaseStyle } from './AlmKindRegistStyle';
import { SettingsState } from '../../../organisms/SelectSettings';
import { ALM_KIND_SET, GetAlmKindSetType, SetAlmKindSetType } from '../../../../function/AlmKindSetModel';
import { useModal } from '../../../../function/useModal';
import { MST_CLC_TYP, GetMstClcTyp } from '../../../../function/MstClcTypModels';
import ConfirmDialog, { ConfirmDialogResponse } from '../../../atoms/Dialog/ConfirmDialog';
import MessageDialog, { MessageDialogResponse } from '../../../atoms/Dialog/MessageDialog';
import { useTranslation } from 'react-i18next';
import { ComboBoxItemType } from '../../../atoms/ComboBox/ItemComboBox';
import { ErrorCode } from '../../../../ErrorCode';

type AlmKindRegistProps = {
  editData: SettingsState;
};

const AlmKindRegistPage: React.FC<AlmKindRegistProps> = (props: AlmKindRegistProps) => {
  const { t } = useTranslation();
  const classes = TableBaseStyle();
  const [itemList, setItemList] = useState<ALM_KIND_SET[]>([]);
  const [mclcItemList, setMclcItemList] = useState<MST_CLC_TYP[]>([]);
  const [mclcComboList, setMclcComboList] = useState<ComboBoxItemType[]>([]);
  const [flgCfmDlg, actCfmDlg] = useModal<ConfirmDialogResponse>();
  const [flgMsgDlg, actMsgDlg] = useModal<MessageDialogResponse>();
  const [dlgmsg, setDlgmsg] = useState<string>('');
  const [buttonEnable, setButtonEnable] = useState<boolean>(true);

  const cfmdlg = flgCfmDlg ? (
    <ConfirmDialog
      dialog={actCfmDlg}
      message={dlgmsg}
      action={t('AlmKindRegistPage.はい', 'はい')}
      cancel={t('AlmKindRegistPage.いいえ', 'いいえ')}
    />
  ) : null;
  const msgdlg = flgMsgDlg ? <MessageDialog dialog={actMsgDlg} message={dlgmsg} /> : null;

  /**
   * 初期表示
   */
  useEffect(() => {
    updateData();
  }, []);

  useEffect(() => {
    props.editData.SaveFunction = saveProc;
    props.editData.IsUpdate = false;
  }, [props.editData.CancelCondition]);

  /**
   *
   * @param item
   */
  const updateDev = (item: ALM_KIND_SET) => {
    const idx = itemList.findIndex((srch) => srch.TYP_NO == item.TYP_NO);
    if (idx >= 0) {
      item.isEdit = true;
      itemList[idx] = item;
      props.editData.Target = itemList;
      props.editData.IsUpdate = true;
    }
  };
  /**
   * 各データの呼び出し
   */
  const updateData = () => {
    setButtonEnable(false);
    let mstClcTyp: MST_CLC_TYP[] = [];
    let mstClcTypList: ComboBoxItemType[] = [];

    GetMstClcTyp()
      .then((res) => {
        mstClcTyp = res;
        mstClcTypList.push({ index: 0, name: '' });

        mstClcTyp.forEach((r) => {
          if (r.SORT_NO !== null) {
            mstClcTypList.push({ index: r.MCLC_TYP_ID, name: r.MCLC_TYP_NAME });
          }
        });

        setMclcItemList(mstClcTyp);
        setMclcComboList(mstClcTypList);

        return GetAlmKindSetType();
      })
      .then((res) => {
        setItemList(res);
      })
      .catch((r) => {
        setDlgmsg(t('AlmKindRegistPage.データ取得中にエラーが発生しました', 'データ取得中にエラーが発生しました'));
        actMsgDlg.open();
        console.error(['saveAction error', r]);
      })
      .finally(() => {
        setButtonEnable(true);
      });
    props.editData.IsUpdate = false;
  };

  /**
   * 入力チェック
   * @param target
   * @returns
   */
  const checkAlmKindRegistFlg = (target: ALM_KIND_SET[]): boolean => {
    if (target == null || target.length == 0) {
      return false;
    }
    if (target.findIndex((r) => r.isEdit == true) === -1) {
      return false;
    }

    return true;
  };

  /**
   * 異常種別登録チェック
   * @param target
   * @returns
   */
  const checkTypeName = (target: ALM_KIND_SET[]): string[] => {
    let message: string[] = [];

    target.forEach((r) => {
      if (r.MCLC_TYP_ID !== null) {
        if (r.MCLC_TYP_ID === 0) {
          if (r.TYP_NAME.trim() !== '' || r.GUIDE_MSG.trim() !== '' || r.GUIDE_MSGL.trim() !== '') {
            let errorMessage =
              `No.${r.TYP_NO}：` + t('AlmKindRegistPage.演算種別を設定してください', '演算種別を設定してください');
            message.push(errorMessage);
          }
        } else {
          if (r.TYP_NAME === '') {
            let errorMessage =
              `No.${r.TYP_NO}` +
              t('AlmKindRegistPage.の異常内容が登録されていません', 'の異常内容が登録されていません');
            message.push(errorMessage);
          }
        }
      }
      else {
        if (r.TYP_NAME.trim() !== '' || r.GUIDE_MSG.trim() !== '' || r.GUIDE_MSGL.trim() !== '') {
          let errorMessage =
            `No.${r.TYP_NO}：` + t('AlmKindRegistPage.演算種別を設定してください', '演算種別を設定してください');
          message.push(errorMessage);
        }
      }
    });
    return message;
  };

  /**
   * 保存アクション
   */
  const saveAction = () => {
    if (checkAlmKindRegistFlg(itemList) === false) {
      return;
    }
    let message = checkTypeName(itemList);
    if (message.length !== 0) {
      setDlgmsg(message.join('\n'));
      actMsgDlg.open();
      return;
    }

    setDlgmsg(t('AlmKindRegistPage.保存しますか', '保存しますか?'));
    actCfmDlg
      .open()
      .apply((_) => {
        let editList = itemList.filter((r) => r.isEdit === true);
        setButtonEnable(false);
        saveProc(editList);
      })
      .cancel(() => {
        console.debug(t('AlmKindRegistPage.キャンセル', 'キャンセル'));
      });
  };

  /**
   * 保存処理
   * @param target
   * @returns
   */
  const saveProc = (target: object): Promise<string> => {
    return new Promise((resolve, reject) => {
      const itemList = target as ALM_KIND_SET[];
      let message = checkTypeName(itemList);
      if (message.length !== 0) {
        setDlgmsg(message.join('\n'));
        actMsgDlg.open();
        reject('data error');
        return;
      }

      SetAlmKindSetType(itemList)
        .then((r) => {
          if (r.ErrorCode === ErrorCode.ERR_CODE_NONE) {
            setDlgmsg(t('AlmKindRegistPage.保存が正常に終了しました', '保存が正常に終了しました'));
            actMsgDlg.open();
            updateData();
            console.debug('saveAction ok');
            resolve('done');
          } else {
            setDlgmsg(t('AlmKindRegistPage.異常が発生したため中断しました', '異常が発生したため中断しました'));
            actMsgDlg.open();
            reject('data error');
          }
        })
        .catch((r) => {
          setDlgmsg(t('AlmKindRegistPage.保存中にエラーが発生しました', '保存中にエラーが発生しました'));
          actMsgDlg.open();
          console.error(['saveAction error', r]);
          reject('data error');
        })
        .finally(() => {
          setButtonEnable(true);
        });
    });
  };

  /**
   * 中止アクション
   */
  const cancelAction = () => {
    if (checkAlmKindRegistFlg(itemList)) {
      if (props.editData.IsUpdate) {
        setDlgmsg(
          t(
            'AlmKindRegistPage.保存していないデータを破棄します。\nよろしいですか?',
            '保存していないデータを破棄します。\nよろしいですか?'
          )
        );
        actCfmDlg
          .open()
          .apply((_) => {
            updateData();
            setItemList([]);
            props.editData.UpdateCancel();
          })
          .cancel(() => { });
      }
    }
  };

  return (
    <>
      <Grid container justify="flex-start" spacing={1} direction="row" alignItems="center" style={{ margin: 2 }}>
        <Grid item xs={12}>
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table aria-label="alarmKind" stickyHeader size="small">
              <TableHead>
                <AlmKindRegistGridHeader />
              </TableHead>
              <TableBody>
                {itemList.map((dev) => {
                  return (
                    <AlmKindRegistGridBody almKindSetItem={dev} updateItem={updateDev} mclcTypList={mclcComboList} />
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={12}>
          <AlmKindRegistFooter
            onClickSaveButton={saveAction}
            onClickCancelButton={cancelAction}
            buttonEnable={buttonEnable}
          ></AlmKindRegistFooter>
        </Grid>
      </Grid>
      {cfmdlg}
      {msgdlg}
    </>
  );
};

export default AlmKindRegistPage;
